/**
 * O período que a linha da capacidade lê: a faixa de semanas que o bloco de ocupação e a
 * pizza de marcas compartilham.
 *
 * Mora fora das telas porque ele vive na barra de endereço (`?periodo=`): o recorte tem
 * de ser o MESMO nos dois blocos e tem de caber num link, e isso é aritmética de
 * semanas, não estado de componente.
 */
import { GradeSemana } from '@/domain/mapa/interfaces/mapa';

/** Quantas semanas cada atalho abre. */
export const SEMANAS_CURTO = 4;
export const SEMANAS_MEDIO = 12;

export type TPeriodoAtalho = 'QUATRO' | 'DOZE' | 'TUDO';

/** O período escolhido: um dos atalhos, ou um intervalo de datas digitado. */
export interface IPeriodoDaVisao {
  atalho: TPeriodoAtalho | null;
  /** Só quando `atalho` é `null`: as duas pontas do "de / até", em ISO. */
  de: string | null;
  ate: string | null;
}

/** O valor inicial, combinado com a Flávia: as próximas 12 semanas. */
export const PERIODO_PADRAO: IPeriodoDaVisao = { atalho: 'DOZE', de: null, ate: null };

const ATALHO_NA_URL: Readonly<Record<TPeriodoAtalho, string>> = {
  QUATRO: '4s',
  DOZE: '12s',
  TUDO: 'tudo',
};

/** O separador do intervalo no link: `?periodo=2026-10-19..2026-12-31`. */
const SEPARADOR = '..';

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** O que o link carrega. O intervalo vai pelas duas datas, que já comparam como texto. */
export function periodoParaUrl(periodo: IPeriodoDaVisao): string {
  if (periodo.atalho !== null) {
    return ATALHO_NA_URL[periodo.atalho];
  }
  return `${periodo.de ?? ''}${SEPARADOR}${periodo.ate ?? ''}`;
}

/**
 * O período que o link pediu. Qualquer coisa ilegível vira o padrão, em vez de uma tela
 * vazia: o link é digitável, e um `?periodo=` torto não pode apagar os dois blocos.
 */
export function periodoDaUrl(valor: string): IPeriodoDaVisao {
  const bruto = valor.trim();
  for (const atalho of Object.keys(ATALHO_NA_URL) as Array<TPeriodoAtalho>) {
    if (ATALHO_NA_URL[atalho] === bruto) {
      return { atalho, de: null, ate: null };
    }
  }

  const [de, ate] = bruto.split(SEPARADOR);
  if (de !== undefined && ate !== undefined && ISO.test(de) && ISO.test(ate) && de <= ate) {
    return { atalho: null, de, ate };
  }
  return PERIODO_PADRAO;
}

/**
 * As semanas da grade que caem no período.
 *
 * Os atalhos contam a partir da semana de HOJE (a que a grade marca como atual), e não do
 * começo do horizonte: "próximas 4 semanas" que começasse numa semana já vencida
 * responderia sobre capacidade que não existe mais. Sem semana atual na grade, a contagem
 * começa no início do horizonte, que é o que sobra para contar.
 */
export function semanasDoPeriodo(
  semanas: Array<GradeSemana>,
  periodo: IPeriodoDaVisao,
): Array<GradeSemana> {
  if (periodo.atalho === 'TUDO') {
    return semanas;
  }

  if (periodo.atalho === null) {
    const de = periodo.de;
    const ate = periodo.ate;
    if (de === null || ate === null) {
      return semanas;
    }
    // A semana entra quando ENCOSTA no intervalo: a que começa antes do "de" mas termina
    // dentro dele tem carga no período, e cortá-la esconderia essa carga.
    return semanas.filter((semana) => semana.fim >= de && semana.inicio <= ate);
  }

  const daAtual = Math.max(
    0,
    semanas.findIndex((semana) => semana.atual),
  );
  const quantas = periodo.atalho === 'QUATRO' ? SEMANAS_CURTO : SEMANAS_MEDIO;
  return semanas.slice(daAtual, daAtual + quantas);
}
